import type { Theme } from "@mui/material";
import {
	BarChart3Icon,
	BitcoinIcon,
	CarIcon,
	GemIcon,
	HouseIcon,
	LandmarkIcon,
	type LucideIcon,
	PaletteIcon,
	PiggyBankIcon,
	ShieldIcon,
	ShoppingCartIcon,
} from "lucide-react";
import {
	ClientPortfolioDomainEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";

type Scale = "blue" | "purple" | "green" | "amber" | "teal" | "indigo";

type IconStyleEntry = { scale: Scale; Icon: LucideIcon };

export type PortfolioRedesignIconStyle = {
	Icon: LucideIcon;
	bg: string;
	color: string;
};

const PROPERTY_TYPE_ICON: Record<ClientPropertyTypeEnum, IconStyleEntry> = {
	[ClientPropertyTypeEnum.AutoMoto]: { scale: "amber", Icon: CarIcon },
	[ClientPropertyTypeEnum.Art]: { scale: "purple", Icon: PaletteIcon },
	[ClientPropertyTypeEnum.Commodity]: { scale: "amber", Icon: GemIcon },
	[ClientPropertyTypeEnum.Flat]: { scale: "blue", Icon: HouseIcon },
	[ClientPropertyTypeEnum.House]: { scale: "blue", Icon: HouseIcon },
	[ClientPropertyTypeEnum.Cottage]: { scale: "blue", Icon: HouseIcon },
	[ClientPropertyTypeEnum.Land]: { scale: "blue", Icon: HouseIcon },
};

const PORTFOLIO_DOMAIN_ICON: Record<ClientPortfolioDomainEnum, IconStyleEntry> =
	{
		[ClientPortfolioDomainEnum.LifeInsurance]: {
			scale: "green",
			Icon: ShieldIcon,
		},
		[ClientPortfolioDomainEnum.PropertyInsurance]: {
			scale: "green",
			Icon: ShieldIcon,
		},
		[ClientPortfolioDomainEnum.Investments]: {
			scale: "purple",
			Icon: BarChart3Icon,
		},
		[ClientPortfolioDomainEnum.Commodities]: {
			scale: "purple",
			Icon: BarChart3Icon,
		},
		[ClientPortfolioDomainEnum.PensionProducts]: {
			scale: "amber",
			Icon: PiggyBankIcon,
		},
		[ClientPortfolioDomainEnum.BuildingSavings]: {
			scale: "amber",
			Icon: PiggyBankIcon,
		},
		[ClientPortfolioDomainEnum.Loans]: { scale: "blue", Icon: LandmarkIcon },
		[ClientPortfolioDomainEnum.BusinessLoans]: {
			scale: "blue",
			Icon: LandmarkIcon,
		},
		[ClientPortfolioDomainEnum.Leasing]: { scale: "blue", Icon: LandmarkIcon },
		[ClientPortfolioDomainEnum.BankingProducts]: {
			scale: "blue",
			Icon: LandmarkIcon,
		},
		[ClientPortfolioDomainEnum.Cryptocurrencies]: {
			scale: "indigo",
			Icon: BitcoinIcon,
		},
		[ClientPortfolioDomainEnum.Services]: {
			scale: "teal",
			Icon: ShoppingCartIcon,
		},
		[ClientPortfolioDomainEnum.PaidAdvisory]: {
			scale: "teal",
			Icon: ShoppingCartIcon,
		},
	};

const FALLBACK_ICON: IconStyleEntry = { scale: "teal", Icon: ShoppingCartIcon };

export const getPortfolioRedesignIconStyle = (
	theme: Theme,
	propertyType?: ClientPropertyTypeEnum | null,
	portfolioType?: ClientPortfolioDomainEnum | null,
): PortfolioRedesignIconStyle => {
	const entry =
		(propertyType && PROPERTY_TYPE_ICON[propertyType]) ||
		(portfolioType && PORTFOLIO_DOMAIN_ICON[portfolioType]) ||
		FALLBACK_ICON;

	return {
		Icon: entry.Icon,
		bg: theme.palette[entry.scale][50],
		color: theme.palette[entry.scale][600],
	};
};
